import React, { useState, useEffect, useCallback } from 'react';
import { useSpaces } from '../context/SpaceContext';
import { getMyCalendar } from '../services/meService';
import { createEvent, deleteEvent } from '../services/calendarService';
import './CalendarPage.css';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];

function daysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
function firstDayOfMonth(y, m) { return new Date(y, m, 1).getDay(); }
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

const EVENT_COLORS = {
  calendarevent: '#3182ce',
  task: '#805ad5',
  reportingobligation: '#dd6b20',
  private: '#805ad5',
  shared: '#3182ce',
  project: '#38a169',
  committee: '#dd6b20',
  resource: '#718096',
};

const BLANK_FORM = { title: '', spaceId: '', start: '', end: '', allDay: false, type: 'shared', description: '' };

export default function CalendarPage() {
  const { spaces } = useSpaces();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [items, setItems] = useState([]);
  const [selectedDay, setSelectedDay] = useState(today);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyCalendar();
      setItems(res.data.items || []);
    } catch (err) {
      console.error('Failed to fetch calendar items:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  // Pre-fill space when spaces load
  useEffect(() => {
    if (spaces.length > 0 && !form.spaceId) {
      setForm((f) => ({ ...f, spaceId: spaces[0]._id }));
    }
  }, [spaces, form.spaceId]);

  const prevMonth = () => {
    if (month === 0) { setYear((y) => y - 1); setMonth(11); }
    else setMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setYear((y) => y + 1); setMonth(0); }
    else setMonth((m) => m + 1);
  };

  const totalDays = daysInMonth(year, month);
  const offset = firstDayOfMonth(year, month);
  const cells = [
    ...Array(offset).fill(null),
    ...Array.from({ length: totalDays }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const filteredItems = items.filter((item) => {
    if (typeFilter === 'all') return true;
    return (item.sourceType || '').toLowerCase() === typeFilter.toLowerCase();
  });

  const itemsOnDay = (d) => {
    if (!d) return [];
    const day = new Date(year, month, d);
    return filteredItems.filter((e) => isSameDay(new Date(e.date || e.start), day));
  };

  const dayItems = filteredItems.filter((e) => isSameDay(new Date(e.date || e.start), selectedDay));

  const openNewForm = () => {
    const pad = (n) => String(n).padStart(2, '0');
    const y = selectedDay.getFullYear(), mo = selectedDay.getMonth() + 1, d = selectedDay.getDate();
    const startStr = `${y}-${pad(mo)}-${pad(d)}T09:00`;
    const endStr   = `${y}-${pad(mo)}-${pad(d)}T10:00`;
    setForm((f) => ({ ...BLANK_FORM, spaceId: f.spaceId || spaces[0]?._id || '', start: startStr, end: endStr }));
    setFormError('');
    setShowForm(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.title.trim() || !form.spaceId || !form.start) {
      setFormError('Title, space, and start are required.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, end: form.end || form.start };
      await createEvent(payload);
      await fetchCalendar();
      setShowForm(false);
    } catch (err) {
      setFormError(err?.response?.data?.message || 'Failed to create event.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, sourceType) => {
    if (sourceType && sourceType !== 'CalendarEvent') {
      alert(`This item is a projected ${sourceType}. Manage it directly in its source Space / Project.`);
      return;
    }
    if (!window.confirm('Delete this calendar event?')) return;
    try {
      await deleteEvent(id);
      setItems((prev) => prev.filter((e) => (e.sourceId || e._id) !== id));
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  return (
    <div className="calendar-page">
      <div className="cal-header">
        <div>
          <h1>My Calendar & Deadlines</h1>
          <p className="dashboard-sub">Consolidated schedule aggregating events, task due dates, and reporting obligations</p>
        </div>
        <div className="cal-header-actions">
          <select
            className="form-input form-input-sm"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">All Sources</option>
            <option value="CalendarEvent">Events Only</option>
            <option value="Task">Task Deadlines</option>
            <option value="ReportingObligation">Reporting Obligations</option>
          </select>
          <button className="btn btn-primary" onClick={openNewForm}>+ New Event</button>
        </div>
      </div>

      {showForm && (
        <div className="event-form-card">
          <div className="event-form-title">
            <span>New Calendar Event</span>
            <button className="btn-icon" onClick={() => setShowForm(false)}>✕</button>
          </div>
          <form onSubmit={handleCreate}>
            {formError && <p className="form-error">{formError}</p>}
            <div className="form-row">
              <input
                className="form-input"
                placeholder="Event title *"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
              <select
                className="form-input"
                value={form.spaceId}
                onChange={(e) => setForm((f) => ({ ...f, spaceId: e.target.value }))}
              >
                {spaces.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
              </select>
              <select
                className="form-input form-input-sm"
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              >
                <option value="shared">Shared</option>
                <option value="private">Private</option>
                <option value="project">Project</option>
                <option value="committee">Committee</option>
                <option value="resource">Resource</option>
              </select>
            </div>
            <div className="form-row">
              <label className="form-label">
                Start *
                <input type="datetime-local" className="form-input" value={form.start}
                  onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))} />
              </label>
              <label className="form-label">
                End
                <input type="datetime-local" className="form-input" value={form.end}
                  onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))} />
              </label>
              <label className="form-check">
                <input type="checkbox" checked={form.allDay}
                  onChange={(e) => setForm((f) => ({ ...f, allDay: e.target.checked }))} />
                All day
              </label>
            </div>
            <textarea
              className="form-input"
              placeholder="Description (optional)"
              value={form.description}
              rows={2}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
            <div className="form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Creating...' : 'Create Event'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="cal-nav">
        <button className="btn-icon cal-nav-btn" onClick={prevMonth}>‹</button>
        <span className="cal-month-label">{MONTHS[month]} {year}</span>
        <button className="btn-icon cal-nav-btn" onClick={nextMonth}>›</button>
        {loading && <span className="cal-loading">Loading...</span>}
      </div>

      <div className="cal-grid">
        {DAYS.map((d) => <div key={d} className="cal-day-header">{d}</div>)}
        {cells.map((d, i) => {
          const dayEvts = itemsOnDay(d);
          const isToday = d && isSameDay(new Date(year, month, d), today);
          const isSelected = d && isSameDay(new Date(year, month, d), selectedDay);
          return (
            <div
              key={i}
              className={`cal-cell${!d ? ' empty' : ''}${isToday ? ' today' : ''}${isSelected ? ' selected' : ''}`}
              onClick={() => d && setSelectedDay(new Date(year, month, d))}
            >
              {d && <span className="cal-day-num">{d}</span>}
              <div className="cal-dots">
                {dayEvts.slice(0, 3).map((e, idx) => (
                  <span
                    key={e.sourceId || idx}
                    className="cal-dot"
                    style={{ background: EVENT_COLORS[(e.sourceType || '').toLowerCase()] || '#718096' }}
                    title={`${e.sourceType ? `[${e.sourceType}] ` : ''}${e.title}`}
                  />
                ))}
                {dayEvts.length > 3 && <span className="cal-dot-more">+{dayEvts.length - 3}</span>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="cal-day-panel">
        <div className="cal-day-panel-header">
          <h3>{selectedDay.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</h3>
          <button className="btn btn-sm btn-ghost" onClick={openNewForm}>+ Add Event</button>
        </div>
        {dayItems.length === 0 ? (
          <p className="cal-muted">No events or deadlines this day.</p>
        ) : (
          <ul className="day-event-list">
            {dayItems.map((e, idx) => {
              const sType = e.sourceType || 'CalendarEvent';
              const sColor = EVENT_COLORS[sType.toLowerCase()] || '#718096';
              return (
                <li key={e.sourceId || idx} className="day-event-item">
                  <span className="day-event-bar" style={{ background: sColor }} />
                  <div className="day-event-info">
                    <div className="day-event-title-row">
                      <span className="day-event-title">{e.title}</span>
                      <span className="cal-type-pill" style={{ borderColor: sColor, color: sColor }}>{sType}</span>
                    </div>
                    <span className="day-event-time">
                      {e.spaceName && <span className="cal-context-tag">{e.spaceName} • </span>}
                      {new Date(e.date || e.start).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  {sType === 'CalendarEvent' && (
                    <button className="btn-icon btn-danger" onClick={() => handleDelete(e.sourceId || e._id, sType)} title="Delete">✕</button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
